import * as tus from "tus-js-client";
import { SUPABASE_URL, adminApi, adminStorageApi, getAccessToken, publicApi } from "./supabase";

const freeDevelopmentStorage = process.env.NODE_ENV === 'development'
  && process.env.REACT_APP_STORAGE_FREE_TIER === 'true';

export const PRESSKIT_LIMITS = {
  image: 25 * 1024 * 1024,
  video: (freeDevelopmentStorage ? 50 : 250) * 1024 * 1024,
};

const allowedTypes = {
  image: new Set(["image/jpeg", "image/png", "image/webp"]),
  video: new Set(["video/mp4", "video/webm"]),
};

const resumableThreshold = 6 * 1024 * 1024;
const resumableEndpoint = `${SUPABASE_URL.replace('.supabase.co', '.storage.supabase.co')}/storage/v1/upload/resumable`;

const extensionByType = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "video/webm": "webm",
};

const encodeStoragePath = (path) => path.split("/").map(encodeURIComponent).join("/");

function normalizeAsset(row) {
  return {
    id: row.id,
    mediaType: row.media_type,
    bucketId: row.bucket_id,
    objectPath: row.object_path,
    altText: row.alt_text,
    mimeType: row.mime_type,
    sizeBytes: Number(row.size_bytes || 0),
    width: row.width,
    height: row.height,
    durationSeconds: row.duration_seconds === null ? null : Number(row.duration_seconds),
    displayOrder: row.display_order,
    isVisible: row.is_visible,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function serializeChanges(changes) {
  const fieldMap = {
    altText: "alt_text",
    mimeType: "mime_type",
    sizeBytes: "size_bytes",
    width: "width",
    height: "height",
    durationSeconds: "duration_seconds",
    displayOrder: "display_order",
    isVisible: "is_visible",
    bucketId: "bucket_id",
    objectPath: "object_path",
    mediaType: "media_type",
  };
  return Object.fromEntries(
    Object.entries(changes).map(([key, value]) => [fieldMap[key] || key, value])
  );
}

export function getPresskitMediaType(file) {
  if (allowedTypes.image.has(file.type)) return "image";
  if (allowedTypes.video.has(file.type)) return "video";
  throw new Error("Use a JPEG, PNG, WebP, MP4, or WebM file.");
}

export function validatePresskitFile(file, requiredType) {
  if (!file) throw new Error("Choose a media file.");
  const mediaType = getPresskitMediaType(file);
  if (requiredType && mediaType !== requiredType) {
    throw new Error(`Choose a replacement ${requiredType === "image" ? "photo" : "video"}.`);
  }
  if (file.size > PRESSKIT_LIMITS[mediaType]) {
    throw new Error(`${mediaType === "image" ? "Photos" : "Videos"} must be no larger than ${formatFileSize(PRESSKIT_LIMITS[mediaType])}.`);
  }
  return mediaType;
}

export function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "Size unavailable";
  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / (1024 ** unitIndex);
  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

export function formatDimensions(asset) {
  return asset.width && asset.height ? `${asset.width} × ${asset.height}` : "Dimensions loading…";
}

export function presskitObjectUrl(asset, download = false) {
  const encodedPath = encodeStoragePath(asset.objectPath);
  const url = `${SUPABASE_URL}/storage/v1/object/public/${asset.bucketId}/${encodedPath}`;
  return download ? `${url}?download` : url;
}

export function presskitThumbnailUrl(asset) {
  if (asset.mediaType !== "image" || freeDevelopmentStorage) return presskitObjectUrl(asset);
  return `${SUPABASE_URL}/storage/v1/render/image/public/${asset.bucketId}/${encodeStoragePath(asset.objectPath)}?width=480&height=480&resize=cover&quality=78`;
}

export async function readMediaMetadata(file) {
  const mediaType = validatePresskitFile(file);
  const objectUrl = URL.createObjectURL(file);

  try {
    if (mediaType === "image") {
      return await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight, durationSeconds: null });
        image.onerror = () => reject(new Error("The selected photo could not be read."));
        image.src = objectUrl;
      });
    }

    return await new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        durationSeconds: Number.isFinite(video.duration) ? video.duration : null,
      });
      video.onerror = () => reject(new Error("The selected video could not be read."));
      video.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function fetchPresskit() {
  const rows = await publicApi(
    "presskit_assets?select=*&is_visible=eq.true&order=media_type,display_order,created_at"
  );
  return rows.map(normalizeAsset);
}

export async function fetchAdminPresskitAssets() {
  const rows = await adminApi("presskit_assets?select=*&order=media_type,display_order,created_at");
  return rows.map(normalizeAsset);
}

export async function updatePresskitAsset(id, changes) {
  const rows = await adminApi(`presskit_assets?id=eq.${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { Prefer: "return=representation" },
    body: JSON.stringify(serializeChanges(changes)),
  });
  if (!rows[0]) throw new Error("The media item could not be updated.");
  return normalizeAsset(rows[0]);
}

export async function reorderPresskitAssets(ids) {
  await adminApi("rpc/reorder_presskit_assets", {
    method: "POST",
    body: JSON.stringify({ asset_ids: ids }),
  });
}

function createObjectPath(file) {
  const extension = extensionByType[file.type];
  const baseName = file.name
    .replace(/\.[^.]+$/, "")
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 70) || "press-media";
  const uniquePart = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `presskit/${baseName}-${uniquePart}.${extension}`;
}

async function uploadObject(bucketId, objectPath, file, onProgress) {
  if (file.size <= resumableThreshold) {
    const result = await adminStorageApi(`object/${bucketId}/${encodeStoragePath(objectPath)}`, {
      method: "POST",
      headers: {
        "Content-Type": file.type,
        "cache-control": "31536000",
        "x-upsert": "false",
      },
      body: file,
    });
    onProgress?.(100);
    return result;
  }

  const accessToken = await getAccessToken();
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: resumableEndpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        authorization: `Bearer ${accessToken}`,
        "x-upsert": "false",
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      chunkSize: resumableThreshold,
      metadata: {
        bucketName: bucketId,
        objectName: objectPath,
        contentType: file.type,
        cacheControl: "31536000",
      },
      onError: reject,
      onProgress: (bytesUploaded, bytesTotal) => {
        onProgress?.(Math.round((bytesUploaded / bytesTotal) * 100));
      },
      onSuccess: () => resolve({ path: objectPath }),
    });

    upload.findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length) upload.resumeFromPreviousUpload(previousUploads[0]);
        upload.start();
      })
      .catch(reject);
  });
}

async function deleteObject(bucketId, objectPath) {
  return adminStorageApi(`object/${bucketId}/${encodeStoragePath(objectPath)}`, { method: "DELETE" });
}

export async function uploadPresskitAsset({ file, altText, displayOrder = 0, onProgress }) {
  const mediaType = validatePresskitFile(file);
  const cleanAltText = altText.trim();
  if (!cleanAltText) throw new Error(mediaType === "image" ? "Add descriptive alt text." : "Add a video description.");
  const metadata = await readMediaMetadata(file);
  const bucketId = mediaType === "image" ? "images" : "videos";
  const objectPath = createObjectPath(file);

  await uploadObject(bucketId, objectPath, file, onProgress);
  try {
    const rows = await adminApi("presskit_assets", {
      method: "POST",
      headers: { Prefer: "return=representation" },
      body: JSON.stringify({
        media_type: mediaType,
        bucket_id: bucketId,
        object_path: objectPath,
        alt_text: cleanAltText,
        mime_type: file.type,
        size_bytes: file.size,
        width: metadata.width,
        height: metadata.height,
        duration_seconds: metadata.durationSeconds,
        display_order: displayOrder,
        is_visible: true,
      }),
    });
    return normalizeAsset(rows[0]);
  } catch (error) {
    await deleteObject(bucketId, objectPath).catch(() => {});
    throw error;
  }
}

export async function replacePresskitAsset(asset, file, onProgress) {
  const mediaType = validatePresskitFile(file, asset.mediaType);
  const metadata = await readMediaMetadata(file);
  const bucketId = mediaType === "image" ? "images" : "videos";
  const objectPath = createObjectPath(file);

  await uploadObject(bucketId, objectPath, file, onProgress);
  try {
    const updated = await updatePresskitAsset(asset.id, {
      bucketId,
      objectPath,
      mimeType: file.type,
      sizeBytes: file.size,
      width: metadata.width,
      height: metadata.height,
      durationSeconds: metadata.durationSeconds,
    });
    await deleteObject(asset.bucketId, asset.objectPath).catch(() => {});
    return updated;
  } catch (error) {
    await deleteObject(bucketId, objectPath).catch(() => {});
    throw error;
  }
}

export async function deletePresskitAsset(asset) {
  await adminApi(`presskit_assets?id=eq.${encodeURIComponent(asset.id)}`, {
    method: "DELETE",
  });
  await deleteObject(asset.bucketId, asset.objectPath).catch(() => {});
}
